/**
 * Shared attachment helpers for content modules (documents, notices, articles, …).
 * Reads legacy `fileUrl` and new `attachments[]` consistently.
 */

export function inferAttachmentNameFromUrl(url, fallback = 'datoteka') {
  if (!url) {
    return fallback;
  }

  try {
    const decoded = decodeURIComponent(String(url).split('?')[0]);
    const segment = decoded.split('/').pop() || fallback;
    const stripped = segment.match(/^[0-9a-f-]{36}_(.+)$/i)?.[1]
      || segment.match(/^[0-9]+-[a-z0-9]+_(.+)$/i)?.[1];
    return stripped || segment || fallback;
  } catch {
    return fallback;
  }
}

export function createAttachmentFromFile(file, url) {
  return {
    name: file?.name || inferAttachmentNameFromUrl(url),
    url,
    ...(file?.type ? { contentType: file.type } : {}),
  };
}

export function normalizeAttachments(item = {}) {
  if (Array.isArray(item.attachments) && item.attachments.length > 0) {
    return item.attachments
      .filter((attachment) => attachment?.url)
      .map((attachment) => ({
        name: attachment.name || inferAttachmentNameFromUrl(attachment.url),
        url: attachment.url,
        ...(attachment.contentType ? { contentType: attachment.contentType } : {}),
      }));
  }

  if (item.fileUrl) {
    return [{
      name: inferAttachmentNameFromUrl(item.fileUrl),
      url: item.fileUrl,
    }];
  }

  return [];
}

/** Persist both attachments and legacy primary fileUrl (first attachment). */
export function syncAttachmentFields(attachments) {
  const normalized = (attachments || [])
    .filter((attachment) => attachment?.url)
    .map((attachment) => ({
      name: attachment.name || inferAttachmentNameFromUrl(attachment.url),
      url: attachment.url,
      ...(attachment.contentType ? { contentType: attachment.contentType } : {}),
    }));

  return {
    attachments: normalized,
    fileUrl: normalized[0]?.url || '',
  };
}

export function attachmentStorageUrls(item = {}) {
  return normalizeAttachments(item).map((attachment) => attachment.url);
}

export function isPdfFile(file) {
  if (!file) {
    return false;
  }
  const type = String(file.type || '').toLowerCase();
  const name = String(file.name || '').toLowerCase();
  return type === 'application/pdf' || name.endsWith('.pdf');
}

export function filterPdfFiles(files) {
  return (files || []).filter(isPdfFile);
}
